import React from 'react'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import type { DividerProps } from './antd-props'
import { Divider as AntdSeamDivider } from './Divider.antd'
import { Divider as ShadcnSeamDivider } from './Divider.shadcn'

/**
 * Compat seam for antd's Divider — one of the six presentational components
 * put behind the design toggle in the same PR (Input, Card, Alert, Divider,
 * Empty, Skeleton), after `../Button` (#970), `../Typography` + `../Space`
 * (#971) and `../Tag` (#972).
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders
 * `./Divider.antd` with the props it was given: same element, same class
 * names, same DOM. The only difference from the previous `./Divider.tsx` —
 * which IS `./Divider.antd`, moved — is one extra component in the React tree,
 * which renders nothing of its own. `./__tests__/divider-seam-parity.test.tsx`
 * asserts that byte for byte across a prop matrix.
 *
 * LIKE `../Tag`, THE ANTD BRANCH IS NOT ANTD. `src/antd` has SHADOWED Divider
 * since long before the toggle existed: `./Divider.antd` forwards exactly nine
 * props and drops everything else, so that a Divider inside a Collapse header
 * does not warn. "Unchanged" therefore means unchanged versus that wrapper,
 * not versus `antd/es/divider`, and the parity test compares against
 * `./Divider.antd` for exactly that reason. The 19 files still importing
 * `Divider` from raw `antd` are the documented deferred residual (AGENTS.md);
 * the toggle does not reach them, by design.
 * ------------------------------------------------------------------------- */
const DividerDispatcher = (props: DividerProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamDivider {...props} />

  return <AntdSeamDivider {...props} />
}

DividerDispatcher.displayName = 'Divider'

export const Divider = DividerDispatcher
